import React, { useState, useEffect, useRef, useContext } from 'react';
import { AutoAwesome, Check } from '@mui/icons-material';
import { ArrowRight, MenuDownArrorw, NewChatLogo } from '../../../../../assets/images';
import { providersTypes } from '../../../llm/pages/llm.props';
import type { LlmPermissionItem } from '../../../../../services';
import { ProviderTabsData } from '../tabs/data/constants';
import { Rag } from '..';
import { chatInitialStateProps } from '../../pages/chat-screen/chat-screen.props';
import { ChatScreenActionTypes } from '../../../../../utils/reducer-types';
import { ChatModelContext, LlmSetupBannerContext } from '../../../../../utils/context';
import makeToast from '../../../../../components/toaster';
import { CreateNewChatSession } from '../../services/chat';
import { getProviders } from '../../../llm/services/llm';
import type { RoutingMode } from '../model-routing-control/model-routing-control';
import {
  isAutoRoutingFeatureEnabled,
  loadRoutingMode,
  saveRoutingMode,
} from '../../lib/routing-mode';

interface ModelTabsProps {
  activeTab: number;
  setActiveTab: (id: number) => void;
  setActiveProvider: (id: number) => void;
  dispatch: (arg: ChatScreenActionTypes) => void;
  activeProviderId: number;
  state: chatInitialStateProps;
  setIsContentAvailable: (arg: boolean) => void;
  onClose: () => void;
  llmPermissions: LlmPermissionItem[];
  llmPermissionsLoaded: boolean;
  /** header: New Chat only. toolbar: compact Auto/model chip in the composer. */
  placement?: 'header' | 'toolbar';
}

function ModelTabs({
  activeTab,
  setActiveTab,
  setActiveProvider,
  dispatch,
  activeProviderId,
  state,
  setIsContentAvailable,
  onClose,
  llmPermissions,
  llmPermissionsLoaded,
  placement = 'header',
}: ModelTabsProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModelsDropdown, setShowModelsDropdown] = useState(false);
  const [allProviders, setAllProviders] = useState<Array<providersTypes & { proverID: number }>>(
    [],
  );
  const { showLlmSetupBanner } = useContext(LlmSetupBannerContext);
  const { chatModel } = useContext(ChatModelContext);
  const [routingMode, setRoutingMode] = useState<RoutingMode>(() => loadRoutingMode());
  const [mcpActive, setMcpActive] = useState(false);
  const autoRoutingUiEnabled = isAutoRoutingFeatureEnabled();

  useEffect(() => {
    const onMcpActive = (event: Event) => {
      const { detail } = event as CustomEvent<boolean>;
      setMcpActive(Boolean(detail));
    };
    window.addEventListener('grigo-chat-mcp-active', onMcpActive);
    return () => window.removeEventListener('grigo-chat-mcp-active', onMcpActive);
  }, []);

  const permissions = localStorage.getItem('configurations');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const modelsDropdownRef = useRef<HTMLDivElement>(null);
  /** Latest tab id so async getProviders() completion does not reset after user picked another provider */
  const activeProviderIdRef = useRef(activeProviderId);
  activeProviderIdRef.current = activeProviderId;

  // Fetch providers from API (merged LLM list comes from chat screen parent)
  useEffect(() => {
    const fetchProviders = async () => {
      if (permissions && llmPermissionsLoaded) {
        try {
          const response = await getProviders();
          if (response.status === 200) {
            const chatPermissions = llmPermissions;
            if (chatPermissions?.length) {
              const uniqueProviders = chatPermissions.reduce(
                (
                  acc: { provider_name: string; proverID: number }[],
                  current: { provider_name: string; proverID: number },
                ) => {
                  if (
                    !acc.find(
                      (item: { provider_name: string }) =>
                        item.provider_name === current.provider_name,
                    )
                  ) {
                    acc.push(current);
                  }
                  return acc;
                },
                [],
              );

              const { data } = response.data;
              const filteredArray =
                data?.filter((item: providersTypes) =>
                  uniqueProviders.some((pro: { proverID: number }) => item.id === pro.proverID),
                ) || [];

              const mappedProviders = filteredArray.map((provider: providersTypes) => ({
                ...provider,
                proverID: provider.id,
              }));

              setAllProviders(mappedProviders);

              if (mappedProviders.length > 0) {
                const idNow = activeProviderIdRef.current;
                const currentStillValid = mappedProviders.some(
                  (p: providersTypes & { proverID: number }) => p.proverID === idNow,
                );
                const openAiProvider = mappedProviders.find(
                  (p: providersTypes & { proverID: number }) =>
                    p.provider_name?.toLowerCase() === 'openai',
                );
                const defaultProvider = openAiProvider ?? mappedProviders[0];
                if (!idNow || !currentStillValid) {
                  setActiveTab(defaultProvider.proverID);
                  setActiveProvider(defaultProvider.proverID);
                  localStorage.setItem('activeProvider', defaultProvider.provider_name ?? '');
                }
              }
            }
          }
        } catch (error) {
          const err = error as { response?: { data?: { message?: string } }; message?: string };
          const msg = err?.response?.data?.message || err?.message || 'Failed to fetch providers';
          makeToast('error', msg);
        }
      }
    };

    fetchProviders();
  }, [permissions, llmPermissions, llmPermissionsLoaded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setShowModelsDropdown(false);
      }
    };
    if (showDropdown || showModelsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, showModelsDropdown]);

  const handleRoutingModeChange = (mode: RoutingMode) => {
    setRoutingMode(mode);
    saveRoutingMode(mode);
    window.dispatchEvent(new CustomEvent('grigo-routing-mode-changed', { detail: mode }));
  };

  const handleProviderClick = (id: number, name: string) => {
    if (routingMode === 'auto') {
      handleRoutingModeChange('manual');
    }
    setActiveTab(id);
    setActiveProvider(id);
    localStorage.setItem('activeProvider', name);
    setShowModelsDropdown(true);
  };

  const handleAutoRowClick = () => {
    handleRoutingModeChange('auto');
    setShowDropdown(false);
    setShowModelsDropdown(false);
  };

  const getProviderLogo = (providerName: string) => {
    const provider = ProviderTabsData.find(
      (item) => item.name?.toLowerCase() === providerName?.toLowerCase(),
    );
    return provider?.logo;
  };

  const handleModelProviderTabChange = (value: boolean) => {
    setShowDropdown(!showDropdown);
    // if (!showDropdown) {
    //   setShowModelsDropdown(value);
    // }
    if (value === false) {
      setShowModelsDropdown(false);
    }
  };

  const resolveSessionModelId = (): number | null => {
    const existingChat = JSON.parse(localStorage.getItem('chat') || '[]');
    const providerObj = existingChat.find(
      (item: { provider_id: number }) => item.provider_id === activeProviderId,
    );
    const modalId = providerObj?.data?.modal?.id as number | undefined;
    if (modalId) {
      return modalId;
    }
    if (routingMode === 'auto' && llmPermissions?.length) {
      const preferred = ['gpt-5.6-terra', 'gpt-5.6-luna', 'gpt-4o'];
      const preferredHit = preferred
        .map((name) =>
          llmPermissions.find(
            (m) => m.name?.toLowerCase() === name.toLowerCase() && m.proverID === activeProviderId,
          ),
        )
        .find((m) => m?.id);
      if (preferredHit?.id) {
        return preferredHit.id;
      }
      const any = llmPermissions.find((m) => m.proverID === activeProviderId);
      return any?.id ?? null;
    }
    return null;
  };

  const handleNewChat = () => {
    const existingChat = JSON.parse(localStorage.getItem('chat') || '[]');
    const modalId = resolveSessionModelId();
    if (!modalId) {
      makeToast('error', 'No model selected for this provider!');
      return;
    }

    CreateNewChatSession({
      model_id: modalId,
      provider_id: activeProviderId || 0,
      routing_mode: loadRoutingMode(),
    })
      .then((res) => {
        if (res.status === 201) {
          const updatedChat = existingChat?.map(
            (item: { data: { chatData: [] }; provider_id: number; session_id: string }) => {
              if (item.provider_id === activeProviderId) {
                return {
                  ...item,
                  session_id: res?.data?.data?.session_id,
                  data: {
                    ...item.data,
                    chatData: [], // Empty the chatData array
                    file_id: undefined, // Clear file_id
                    use_document: false, // Reset use_document to false
                    has_document: false, // Reset has_document to false
                    webSearchEnabled: false,
                    webSearchMode: 'auto',
                  },
                };
              }
              return {
                ...item,
                session_id: item.session_id ? item.session_id : null,
              };
            },
          );
          // setParsedExistingChat(updatedChat);
          localStorage.setItem('chat', JSON.stringify(updatedChat));
          dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
          dispatch({ type: 'SET_WEB_SEARCH_MODE', payload: 'auto' });
          dispatch({ type: 'TOGGLE_DATA_CHANGED' });
          setIsContentAvailable(false);
          onClose();
        }
      })
      .catch((error) => {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const msg = err?.response?.data?.message || err?.message || 'Something went wrong!';
        makeToast('error', msg);
      });
  };

  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const currentProvider = allProviders?.find((p) => p.proverID === activeTab);

  // Get model name from localStorage chat data or chatModel context
  const getSelectedModelName = () => {
    const existingChat = JSON.parse(localStorage.getItem('chat') || '[]');
    const providerData = existingChat.find(
      (item: { provider_id: number }) => item.provider_id === activeTab,
    );
    const modalName = providerData?.data?.modal?.name;

    if (modalName) return modalName;
    if (chatModel) return chatModel.split('|')[0];
    return null;
  };

  const selectedModelName = getSelectedModelName();
  const showAutoInList = autoRoutingUiEnabled;

  const menuOpensUp = placement === 'toolbar';
  const isAutoMode = autoRoutingUiEnabled && routingMode === 'auto';

  return (
    <div className="flex gap-2.5 items-center">
      <div
        className={`relative w-auto ${placement === 'header' ? 'hidden' : ''} ${
          menuOpensUp ? 'max-w-[200px]' : 'min-w-[300px]'
        }`}
        ref={dropdownRef}
      >
        <button
          type="button"
          data-tour="chat-tour-provider-model"
          className="flex items-center h-9 px-2 rounded-full text-[13px] font-medium text-zinc-500 hover:bg-zinc-100 transition-colors w-full min-w-0"
          onClick={() => handleModelProviderTabChange(true)}
          title={currentProvider?.provider_name || 'Select model'}
          aria-expanded={showDropdown}
        >
          <img src={MenuDownArrorw} alt="" className="w-3 h-3 mr-1 shrink-0 opacity-70" />
          {isAutoMode ? (
            <span className="truncate max-w-[120px]">Auto</span>
          ) : (
            <span className="truncate max-w-[120px]">{selectedModelName || 'Select model'}</span>
          )}
        </button>
        <div
          className={`absolute bg-white border border-zinc-200 rounded-2xl z-30 min-w-[240px] p-1.5 transition-all duration-300 ease-in-out overflow-hidden ${
            menuOpensUp ? 'bottom-full right-0 mb-2' : 'top-full left-0 w-full'
          } ${showDropdown ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0 pointer-events-none'}`}
        >
          <div className="flex flex-col gap-0.5">
            {showAutoInList ? (
              <div
                className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl cursor-pointer transition-colors ${
                  routingMode === 'auto'
                    ? 'bg-[#e0f1f8] text-[#0A8BC7]'
                    : 'text-zinc-800 hover:bg-zinc-50'
                }`}
                role="button"
                tabIndex={0}
                onClick={handleAutoRowClick}
                onKeyDown={(e) => handleKeyDown(e, handleAutoRowClick)}
              >
                <div className="w-8 h-8 rounded-full bg-[#0A8BC7] flex items-center justify-center shrink-0">
                  <AutoAwesome sx={{ fontSize: 16, color: '#fff' }} />
                </div>
                <span className="flex-1 text-sm font-medium">Auto</span>
                {routingMode === 'auto' ? <Check sx={{ fontSize: 18 }} /> : null}
              </div>
            ) : null}
            {allProviders.map((provider) => {
              const selected = routingMode !== 'auto' && activeTab === provider.proverID;
              return (
                <div
                  key={provider.proverID}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl cursor-pointer transition-colors ${
                    selected ? 'bg-[#e0f1f8] text-[#0A8BC7]' : 'text-zinc-800 hover:bg-zinc-50'
                  }`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleProviderClick(provider.proverID, provider.provider_name);
                    }
                  }}
                  onClick={() => {
                    handleProviderClick(provider.proverID, provider.provider_name);
                  }}
                >
                  <img
                    src={getProviderLogo(provider.provider_name)}
                    alt=""
                    className="w-8 h-8 shrink-0"
                  />
                  <span className="flex-1 text-sm font-medium truncate">
                    {provider.provider_name}
                  </span>
                  <div
                    className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/70"
                    aria-hidden
                  >
                    <img src={ArrowRight} alt="" className="w-4 h-4" />
                  </div>
                </div>
              );
            })}
            {!showAutoInList && allProviders.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-zinc-400">No providers</div>
            ) : null}
          </div>
        </div>
        {/* Rag Component - positioned beside provider dropdown */}
        <div
          ref={modelsDropdownRef}
          className={`absolute bg-white border border-zinc-200 rounded-2xl z-40 w-72 transition-all duration-300 ease-in-out overflow-hidden ${
            menuOpensUp ? 'bottom-0 right-full mr-2' : 'top-full -right-[385px] ml-2'
          } ${showModelsDropdown ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0'}`}
        >
          <div className="p-1.5">
            <Rag
              dispatch={dispatch}
              state={state}
              activeProviderId={activeProviderId}
              handleModelProviderTabChange={handleModelProviderTabChange}
              llmPermissions={llmPermissions}
              llmPermissionsLoaded={llmPermissionsLoaded}
              mcpActive={mcpActive}
            />
          </div>
        </div>
      </div>

      <button
        type="button"
        data-tour="chat-tour-new-chat"
        className={`${
          placement === 'toolbar' ? 'hidden' : 'flex'
        } items-center h-9 px-3.5 rounded-full bg-[#FFF4E5] text-[#F48B00] hover:bg-[#FFE8CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        tabIndex={0}
        onClick={handleNewChat}
        onKeyDown={(e) => handleKeyDown(e, handleNewChat)}
        disabled={showLlmSetupBanner}
        title={
          showLlmSetupBanner
            ? 'Please add at least one provider or LLM model for your account to create a new chat'
            : ''
        }
      >
        <img
          src={NewChatLogo}
          alt=""
          className="w-4 h-4 mr-2"
          style={{
            filter:
              'brightness(0) saturate(100%) invert(56%) sepia(98%) saturate(1000%) hue-rotate(360deg) brightness(103%) contrast(101%)',
          }}
        />
        <span className="text-[13px] font-medium text-[#F48B00]">New Chat</span>
      </button>
    </div>
  );
}

ModelTabs.defaultProps = {
  placement: 'header',
};

export default ModelTabs;
